import { withRouter } from "react-router-dom";
import { connect, useSelector, useDispatch } from "react-redux";
import { bindActionCreators } from "redux";
import CipHeader from "../Components/CipHeader";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Modal } from "react-responsive-modal";
import medpassService from "../services/emar-medpass-service";
import cogoToast from "cogo-toast";
import Loader from "react-loader-spinner";
import { selectUsers } from "../state/reducers/userSlice";
import { updateSelectedPatient, clearSelectedPatient, updateFilters, clearFilters, selectPatient } from "../state/reducers/emarSlice";
import NO_PATIENTS from "../assets/img/no-patients.png";
import PAUSE from "../assets/img/pause.svg";
import React, { useState, useEffect, createRef, useRef } from "react";
import moment from "moment";
import { Multiselect } from "multiselect-react-dropdown";
import Select from 'react-dropdown-select';
import facilityService from "../services/facility-service";
import emarMedpassService from "../services/emar-medpass-service";
import emarAddendumMedpassService from "../services/emar-addendum-medpass-service";

const AddendumMedpassAdministration = ({ history }) => {
  const { user } = useSelector(selectUsers);
  const { filters } = useSelector(selectPatient);
  const dispatchEmar = useDispatch();
  const multiselectRef = createRef();
  const [patients, setPatients] = useState([]);
  const [patData, setPatData] = useState({});
  const [searchValue, setSearchValue] = useState('');
  const [medDate, setMedDate] = useState(new Date());
  const [selectedTimeText, setSelectedTimeText] = useState([]);
  const [selectedTime, setSelectedTime] = useState(filters?.rangeSelected || []);
  const [passes, setPasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [medReasonList, setMedReasonList] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    patientDetails: true,
    medications: true,
    addendum: true
  });
  const [normalizedMedications, setNormalizedMedications] = useState([]);
  const [adminUpdates, setAdminUpdates] = useState([]);
  const [uiState, setUiState] = useState({});
  const [addendumData, setAddendumData] = useState([]);
  const [showEmptyState, setShowEmptyState] = useState(false);
  const [onshowNotes, setOnshowNotes] = useState(false);
  const [notesData, setNotesData] = useState("");
  const [addendumNotes, setAddendumNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);

    return debouncedValue;
  };

  const debouncedSearchQuery = useDebounce(searchValue, 500); // Delay of 500ms

  useEffect(() => {
    if (debouncedSearchQuery)
      getPatientsBySearch(debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  const getPatientsBySearch = async (search) => {
    try {
      setPatients([])
      setLoading(true);
      const facilityId = user?.facility;
      const searchParam = search?.replace(/^0+/, '');
      const resp = await emarAddendumMedpassService.getAddendumPatientsBySearch(facilityId, searchParam);
      setPatients(resp.map(patient => ({
        ...patient,
        fullname: `${patient.lname},${patient.fname}`,  // Combine lname and fname
      })));
      setLoading(false);
    } catch (e) {
      console.log("e ::: ", e);

      const { hide } = cogoToast.error(
        "Error getting patients. Please try after sometime.",
        {
          hideAfter: 15,
          onClick: () => {
            hide();
          },
        }
      );
    }
  }

  const getDefaultSettings = async () => {
    try {
      const resp = await medpassService.getDefaults(user?.facility);
      dispatchEmar(clearSelectedPatient());
      setPasses(resp?.ranges);
      setMedReasonList(resp?.rejected_reasons);
    } catch (e) {
      const { hide } = cogoToast.error(
        "Error getting data. Please try after sometime.",
        {
          hideAfter: 15,
          onClick: () => {
            hide();
          },
        }
      );
    }
  };

  const getMedKey = (m) => `${m.rxf_id}_${m.rx_number}`;

  const resolveYesStatus = (sourceType) => {
    if (user?.userRole === "EMAR_USER") return "S";
    return sourceType === "medications" ? "A" : "P";
  };

  const normalizeResponse = (resp) => {
    const all = [];

    resp.medications.forEach(m => all.push({ ...m, sourceType: "medications" }));
    resp.active_orders.forEach(m => all.push({ ...m, sourceType: "active_orders" }));
    resp.stat_meds.forEach(m => all.push({ ...m, sourceType: "stat_meds" }));

    const ui = {};
    all.forEach(m => {
      ui[getMedKey(m)] = {
        selectedAction: null,
        doseQty: null,
        reasonId: null
      };
    });

    setUiState(ui);
    setNormalizedMedications(all);
  };

  const upsertAdminObject = (med) => {
    const key = getMedKey(med);
    let obj = adminUpdates.find(o => o._key === key);

    if (!obj) {
      obj = {
        _key: key,
        med_date: moment(medDate).format('MM/DD/YYYY'),
        pat_id: med.pat_id,
        range_id: med.range_id,
        rxf_id: med.rxf_id,
        rx_number: med.rx_number,
        drg_id: med.drg_id,
        status: "",
        med_reason_id: 0,
        reason_id: 0,
        days_supply: med.days_supply,
        dose_taken_qty: 0,
        fil_id: med.fil_id,
        notes: ""
      };
      setAdminUpdates(prev => [...prev, obj]);
    }
    return obj;
  };

  // ---------------- ACTION HANDLERS ----------------
  const onActionClick = (med, action) => {
    const key = getMedKey(med);
    const adminObj = upsertAdminObject(med);
    const initialDoseQty = action === "YES" ? Number(med?.dose_qty ?? 0) : 0;

    setUiState(prev => ({
      ...prev,
      [key]: { selectedAction: action, doseQty: action === "YES" ? initialDoseQty : null, reasonId: null }
    }));

    adminObj.dose_taken_qty = initialDoseQty;
    adminObj.med_reason_id = 0;

    if (action === "YES") adminObj.status = resolveYesStatus(med.sourceType);
    if (action === "NO") adminObj.status = "N";
    if (action === "REFUSED") adminObj.status = "R";
  };

  const onDoseEntered = (med, qty) => {
    const key = getMedKey(med);
    const adminObj = upsertAdminObject(med);

    setUiState(prev => ({
      ...prev,
      [key]: { ...prev[key], doseQty: qty }
    }));

    adminObj.dose_taken_qty = qty;
  };

  const onReasonSelected = (med, reasonId) => {
    const key = getMedKey(med);
    const adminObj = upsertAdminObject(med);

    setUiState(prev => ({
      ...prev,
      [key]: { ...prev[key], reasonId }
    }));

    adminObj.med_reason_id = reasonId;
  };

  const onSubmitMedicationsClick = async () => {
    try {
      if (adminUpdates.length === 0) {
        const { hide } = cogoToast.error(
          "Administer atleast one Medication",
          {
            hideAfter: 15,
            onClick: () => {
              hide();
            },
          }
        );

        return;
      }
      setLoading(true);
      const payload = adminUpdates.map(item => {
        const { _key, ...rest } = item;
        return {
          ...rest,
          notes: notesData
        };
      });
      await emarAddendumMedpassService.postPatientAddendumMedications(payload);
      fetchMedicationData();
      resetUI();
      setLoading(false);
      setOnshowNotes(false);
    } catch (error) {
      console.error("Submission failed:", error);
      setLoading(false);
    }
  };

  const resetUI = () => {
    setAdminUpdates([]);
    setNotesData('');

    setUiState(prev => {
      const reset = {};
      Object.keys(prev).forEach(key => {
        reset[key] = {
          selectedAction: null,
          doseQty: null,
          reasonId: null
        };
      });
      return reset;
    });

  };

  const fetchMedicationData = async () => {
    // Check if all required conditions are met
    if (!patData?.id) {
      cogoToast.warn('Please select a patient');
      return;
    }

    if (!medDate) {
      cogoToast.warn('Please select a date');
      return;
    }

    if (!selectedTime?.length) {
      cogoToast.warn('Please select at least one time range');
      return;
    }

    try {
      setIsLoading(true);

      // Format the date for the API
      const formattedDate = moment(medDate).format('MM/DD/YYYY');

      // Convert selectedTime to numbers if needed
      const passes = selectedTime.map(time => Number(time));

      // Make the API call - replace with your actual API service method
      const response = await emarAddendumMedpassService.getPatientAddendumMedications(
        patData.id,
        formattedDate,
        passes
      );

      if ((response?.medications.length === 0 && response?.active_orders.length === 0 && response?.stat_meds.length === 0)) {
        setShowEmptyState(true);
        setIsLoading(false);
        return;
      }
      // Handle the response
      if (response) {
        setShowEmptyState(false);
        normalizeResponse(response);
        setAddendumData(response.administrations);
        setAddendumNotes(response?.notes || "");
      };

    } catch (error) {
      console.error('Error fetching medication data:', error);
      cogoToast.error('Failed to fetch medication data. Please try again.');
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (patData?.id && medDate && selectedTime?.length) {
      fetchMedicationData();
    }
  }, [patData, medDate, selectedTime]);

  useEffect(() => {
    getDefaultSettings();
  }, [user?.facility]);

  const handleNotesChange = (e) => {
    setNotesData(e.target.value);
  };

  const handleNavigationToHistory = async (item) => {
    try {
      dispatchEmar(updateSelectedPatient({
        ...item,
        pat_id: item?.id,
        pat_name: item?.fullname
      }));
      await history.push("/medhistory");
    } catch (e) {
      setLoading(false);
    }
  };

  return (
    <>
      <CipHeader />
      <div className="bg-white w-11/12 mx-auto mt-6 mb-4 rounded shadow-md ">
        <div className="tableHeader flex clearboth shadow-md py-8 pl-3">
          <div className="w-1/4 text-darkclr font-semibold leading-10 ">
            <span className="text-xl">ADDENDUM MEDPASS</span>
          </div>
          <div className="w-3/4 pr-3 text-right font-semibold ">
            <span className="inline-block mr-3 py-2 w-2/5">
              <div>
                <Multiselect
                  name="patient"
                  displayValue="fullname"
                  options={patients}
                  ref={multiselectRef}
                  closeOnSelect={true}
                  placeholder="Select Patient"
                  selectedValues={patData?.pat_id ? [patData] : []}
                  onSelect={(selectedItem) => {
                    setPatData(selectedItem[0]);
                  }}
                  onRemove={(e) => {
                    setPatData({});
                    setNormalizedMedications([]);
                    setAddendumData([]);
                    setAddendumNotes("");
                    setShowEmptyState(true);
                  }}
                  onSearch={(e) => {
                    setSearchValue(e);
                  }}
                  selectionLimit={1}
                />
              </div>
            </span>
            <span className="inline-block mr-6 py-2 ">
              <div>
                <DatePicker
                  placeholderText="MM/DD/YYYY"
                  selected={medDate}
                  onChange={(date) => {
                    setMedDate(moment(date, "MM-DD-YYYY").toDate());
                  }}
                  className="border focus:outline-none h-11 bg-header_bg text-white rounded-md pl-2 font-normal   text-sm"
                />
              </div>
            </span>
            <span className="inline-block mr-3 py-2 ">
              <div>
                <div className="dropdown">
                  <button className="p-2 dropdownLabel w-32 text-ellipsis rounded text-left">{selectedTimeText?.length > 0 ? selectedTimeText?.join(', ') : "Select time"}</button>
                  <div className="dropdown-content ">
                    {passes
                      ? passes.map((pass, i) => {
                        return (
                          <div key={`${pass.id}_${pass.pat_name}_${Date.now()}_${Math.floor(Math.random() * 1e6)}`} className="float-left text-left	">
                            <input type="checkbox" name={pass.title} data-start={pass?.start_time} className="m-3" onChange={(e) => {
                              if (e.target.checked) {
                                if (selectedTime?.length > 0) {
                                  let passStartTime = passes?.find(ele => ele.id == selectedTime[0])?.start_time;
                                  let start = moment.utc(passStartTime).subtract(4, 'hours').subtract(1, 'minutes');
                                  let end = moment.utc(passStartTime).add(4, 'hours').add(1, 'minutes');
                                  let now = moment.utc(e.target.dataset.start);
                                  if (now.isAfter(start) && now.isBefore(end)) {
                                    setSelectedTime([...selectedTime, e.target.value]);
                                    setSelectedTimeText([...selectedTimeText, e.target.name]);
                                  } else {
                                    const { hide } = cogoToast.error(
                                      "Select appropriate medpasses",
                                      {
                                        hideAfter: 15,
                                        onClick: () => {
                                          hide();
                                        },
                                      }
                                    );
                                  }
                                } else {
                                  setSelectedTime([...selectedTime, e.target.value]);
                                  setSelectedTimeText([...selectedTimeText, e.target.name]);
                                }
                              } else {
                                let filters = selectedTime.filter(item => item !== e.target.value);
                                setSelectedTime(filters);
                                let filterText = selectedTimeText.filter(item => item !== e.target.name);
                                setSelectedTimeText(filterText);
                              }
                            }} value={pass.id} disabled={loading} checked={selectedTime.includes(pass.id + "")}></input> {pass.title}</div>

                        );
                      })
                      : null}
                  </div>
                </div>
              </div>
            </span>
          </div>
        </div>

        <div
          className={`inline-block customtop absolute   z-50 ${isLoading ? "show" : "hidden"
            }`}
        >
          <Loader
            type="Oval"
            color="#00BFFF"
            height={50}
            width={50}
            timeout={300000} //3 secs
          />
        </div>

        {(normalizedMedications.length > 0 && !showEmptyState) && (
          <>
            < div className="p-3 w-full gap-y-4">
              {/* Patient Details Card */}
              <div className="bg-white rounded-lg shadow-xl p-4 mb-4 mx-4">
                <div
                  className="flex justify-between items-center cursor-pointer"
                  onClick={() => setExpandedSections(prev => ({ ...prev, patientDetails: !prev.patientDetails }))}
                >
                  <h2 className="text-lg font-semibold">PATIENT DETAILS</h2>
                  <span>{expandedSections.patientDetails ? '▼' : '▶'}</span>
                </div>

                {expandedSections.patientDetails && patData?.id && (
                  <div className="flex flex-row w-full p-3">
                    <div className="flex-shrink-0">
                      <img
                        src="/img/profile2.svg"
                        alt=""
                        className="w-20 mr-10"
                      />
                    </div>
                    <div className="flex flex-row w-full">
                      <div className="flex flex-col pt-1">
                        <div className="cursor-pointer underline text-xl font-semibold"
                          onClick={() => handleNavigationToHistory(patData)}>
                          {`${patData.lname || ''}, ${patData.fname || ''}`}
                        </div>
                        <span className="text-sm font-semibold">
                          {patData.acct_number || 'N/A'}
                        </span>
                        <div className="text-xs font-semibold">
                          <span>
                            {patData.gender || 'N/A'}
                          </span>
                          <div>
                            DOB: {patData.dob ? moment(patData.dob).format('MM/DD/YYYY') : 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Medications Card */}
              <div className="bg-white rounded-lg shadow-xl p-4 mx-4 mb-4">
                <div
                  className="flex justify-between items-center cursor-pointer"
                  onClick={() => setExpandedSections(prev => ({ ...prev, medications: !prev.medications }))}
                >
                  <h2 className="text-lg font-semibold">MEDICATIONS</h2>
                  <span>{expandedSections.medications ? '▼' : '▶'}</span>
                </div>

                {expandedSections.medications && (
                  <>
                    {normalizedMedications.map(med => {
                      const key = getMedKey(med);
                      const ui = uiState[key];
                      const canShowAdminBlock =
                        med.sourceType !== "active_orders" ||
                        med?.is_admistration_allowed === "T";

                      return (
                        <>
                          <div
                            key={`${key}_${Date.now()}_${Math.floor(Math.random() * 1e6)}`}
                            className="mt-2 focus:scroll-auto text-xs leading-6 text-menu_txt w-full border-inherit medicationList border rounded-xl p-4 mb-4 flex bg-white flex-row gap-2">
                            {/* LEFT */}
                            <div className="w-1/2">
                              <div className="text-base	font-semibold text-black">
                                {med.drg_name} {med?.is_diabetic === 'T' && <span className="bg-slate text-xs	ml-2">Insulin</span>}{med?.is_hypertensive === 'T' && <span className="bg-slate text-xs ml-2	">Blood Pressure</span>}
                                {med?.is_prn === 'T' && <span className="bg-slate text-xs ml-2	">PRN</span>}
                                {med?.is_crush === 'T' && <span className="bg-orange text-xs ml-2	">CRUSH</span>}
                              </div>
                              {med?.status === 'I' && <span className="font-xxs font-semibold text-black w-full inline-block leading-3 relative ">
                                {med.rxf_sig}  {med?.rxf_org_date && `${'Start date: ' + moment.utc(med?.rxf_org_date).format('MM/DD/YYYY')}`} {med?.rxf_exp_date && `${'Exp date: ' + moment.utc(med?.rxf_exp_date).format('MM/DD/YYYY')}`}
                              </span>}
                              {med?.is_diabetic === 'T' && <span className="font-xxs font-semibold text-black w-full inline-block leading-3 relative ">
                                {med?.rxf_expanded_sig}
                              </span>}
                              {med?.status !== 'I' && <span className="font-xxs font-semibold text-black w-full inline-block leading-3 relative  ">
                                {med?.is_diabetic !== 'T' && med.frequency}   {med?.is_diabetic !== 'T' && <span className="font-bold">Qty/Dose: {med?.dose_qty}</span>}  {med?.rxf_org_date && `${'Start date: ' + moment.utc(med?.rxf_org_date).format('MM/DD/YYYY')}`} {med?.rxf_exp_date && `${'Exp date: ' + moment.utc(med?.rxf_exp_date).format('MM/DD/YYYY')}`}
                              </span>}
                              {<span className="font-xxs font-semibold  text-black w-full inline-block leading-3 relative bottom-1">
                                {med.last_admit_date && <span>Last administered: {med.last_admit_date} </span>} {med?.is_kop === 'T' && <span className="font-bold">Qty given: {med?.last_admit_qty}</span>}
                              </span>}
                            </div>

                            {/* RIGHT */}

                            {canShowAdminBlock &&
                              <div className="w-1/2">
                                {!(med?.monthly_drug_admin_limit && med?.current_month_drug_admin_count > med?.monthly_drug_admin_limit) && (
                                  <>
                                    <div>
                                      {med?.monthly_drug_admin_limit > 0 && <div className="text-xs">Administered this month: {med?.current_month_drug_admin_count}</div>}
                                    </div>

                                    <div className="mt-2">
                                      <div className="flex gap-2">
                                        <button
                                          className={` py-2 px-4 text-sm rounded-3xl border-inherit  mr-4 cursor-pointer ${((ui?.selectedAction === "YES")) ? 'bg-mediApproved borderSelected ' : 'bg-white border'} ${med?.isAddendumAdministered && 'opacity-50 cursor-not-allowed'}`}
                                          disabled={med?.isAddendumAdministered}
                                          onClick={() => onActionClick(med, "YES")}>
                                          Yes
                                        </button>
                                        {med.sourceType === "medications" && (
                                          <button
                                            className={` py-2 px-4 text-sm rounded-3xl border-inherit  mr-4 cursor-pointer ${((ui?.selectedAction === "NO")) ? 'bg-mediRejected borderSelected ' : 'bg-white border'} ${med?.isAddendumAdministered && 'opacity-50 cursor-not-allowed'}`}
                                            disabled={med?.isAddendumAdministered}
                                            onClick={() => onActionClick(med, "NO")}>
                                            No
                                          </button>
                                        )}
                                        <button
                                          className={` py-2 px-4 text-sm rounded-3xl border-inherit  mr-4 cursor-pointer ${((ui?.selectedAction === "REFUSED")) ? 'bg-mediRefused borderSelected ' : 'bg-white border'} ${med?.isAddendumAdministered && 'opacity-50 cursor-not-allowed'}`}
                                          disabled={med?.isAddendumAdministered}
                                          onClick={() => onActionClick(med, "REFUSED")}>
                                          Refused
                                        </button>
                                      </div>

                                      <div className="mt-2 flex">
                                        {ui?.selectedAction === "YES" && (
                                          <input
                                            id="doseTakenFilled"
                                            name="doseTakenFilled"
                                            key={getMedKey(med)}
                                            type="number"
                                            className="border mt-2 p-1 w-full"
                                            placeholder="Dose taken"
                                            onBlur={(e) => onDoseEntered(med, Number(e.target.value))}
                                            defaultValue={ui?.doseQty !== null ? ui?.doseQty : (med?.dose_qty ?? "")}
                                          />
                                        )}

                                        {ui?.selectedAction === "NO" && (
                                          <select
                                            name="reason"
                                            aria-label="Select Reason"
                                            value={ui?.reasonId || ""}
                                            onChange={(e) => onReasonSelected(med, Number(e.target.value))}
                                            className="border focus:outline-none h-11 rounded-md pl-2 font-normal w-full text-fogotpwsd_login text-sm"
                                          >
                                            <option value="">Select Reason</option>
                                            {medReasonList?.map((item) => (
                                              <option key={`${item.id}_${Date.now()}_${Math.floor(Math.random() * 1e6)}`} value={item.id}>
                                                {item.reason}
                                              </option>
                                            ))}
                                          </select>
                                        )}
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            }



                          </div>
                        </>
                      );
                    })}

                    {/* Footer */}
                    <div className="flex justify-end border-t pt-4 mt-6">
                      <button
                        onClick={() => {
                          setOnshowNotes(true);
                        }}
                        type="button"
                        disabled={loading}
                        className={
                          loading
                            ? "opacity-50 cursor-not-allowed pointer-events-none mr-0.5 rounded font-medium h-10 text-white w-auto px-6 bg-secondary bg-login_btn focus:outline-none"
                            : "mr-0.5 rounded font-medium h-10 text-white w-auto px-6 bg-secondary bg-login_btn focus:outline-none"
                        }
                      >
                        Submit
                      </button>
                    </div>
                  </>
                )}
              </div>

              {/* Addendum Administered Medications Card */}
              {addendumData.length > 0 && (
                <div className="bg-white rounded-lg shadow-xl p-4 mx-4 mb-4">
                  <div
                    className="flex justify-between items-center cursor-pointer"
                    onClick={() => setExpandedSections(prev => ({ ...prev, addendum: !prev.addendum }))}
                  >
                    <h2 className="text-lg font-semibold">ADDENDUM ADMINISTERED MEDICATIONS</h2>
                    <span>{expandedSections.addendum ? '▼' : '▶'}</span>
                  </div>

                  {expandedSections.addendum && addendumData.length > 0 && (
                    <>

                      {addendumNotes?.length > 0 && (
                        <div className="mt-5 mb-2">
                          <h2 className="text-base	font-semibold text-menuHvrColor"> <span className="font-semibold">Notes:</span> {addendumNotes}</h2>
                        </div>
                      )}

                      {addendumData.map(med => {

                        return (
                          <>
                            <div
                              key={`${med.rxf_id}_${Date.now()}_${Math.floor(Math.random() * 1e6)}`}
                              className="mt-3 focus:scroll-auto text-xs leading-6 text-menu_txt w-full border-inherit medicationList border rounded-xl p-4 mb-4 flex bg-white flex-row gap-2">
                              <div
                                className={`flex w-full rounded-xl ${(med.status === "P" || med.status === "D") ? 'opacity-40 pointer-events-none' : ''} text-black px-3 pt-2  ${(med?.status === "P" || med?.status === "A")
                                  ? "bg-mediApproved"
                                  : med?.status === "S"
                                    ? "bg-mediSelfApproved"
                                    : med?.status === "N"
                                      ? "bg-mediRejected"
                                      : (med?.status === "R" || med?.status === "D")
                                        ? "bg-mediRefused"
                                        : ""
                                  }`}
                              >
                                <div className="w-1/2 flex-column">
                                  <div className="text-base	font-semibold text-black">
                                    {med.drg_name} {med?.is_diabetic === 'T' && <span className="bg-slate text-xs	ml-2">Insulin</span>}{med?.is_hypertensive === 'T' && <span className="bg-slate text-xs ml-2	">Blood Pressure</span>}
                                    {med?.is_prn === 'T' && <span className="bg-slate text-xs ml-2	">PRN</span>}
                                    {med?.is_crush === 'T' && <span className="bg-orange text-xs ml-2	">CRUSH</span>}
                                    {med?.is_involuntary === 'T' && <span className="bg-orderFourhr text-xs ml-2	">INVOLUNTARY</span>}
                                  </div>
                                  {med?.status === 'I' && <span className="font-xxs font-semibold text-black w-full inline-block leading-3 relative ">
                                    {med.rxf_sig} {med?.rxf_org_date && `${'Start date: ' + moment.utc(med?.rxf_org_date).format('MM/DD/YYYY')}`}  {med?.rxf_exp_date && `${'Exp date: ' + moment.utc(med?.rxf_exp_date).format('MM/DD/YYYY')}`}
                                  </span>}
                                  {med?.is_diabetic === 'T' && <span className="font-xxs font-semibold text-black w-full inline-block leading-3 relative ">
                                    {med?.rxf_expanded_sig}
                                  </span>}
                                  {med?.status !== 'I' && <span className="font-xxs font-semibold text-black w-full inline-block leading-3 relative">
                                    {med?.is_diabetic !== 'T' && med.frequency}   {med?.is_diabetic !== 'T' && <span className="font-bold">Qty/Dose: { med?.dose_taken_qty ?? med?.dose_qty}</span>} {med?.rxf_org_date && `${'Start date: ' + moment.utc(med?.rxf_org_date).format('MM/DD/YYYY')}`}  {med?.rxf_exp_date && `${'Exp date: ' + moment.utc(med?.rxf_exp_date).format('MM/DD/YYYY')}`}
                                  </span>}
                                  {<span className="font-xxs font-semibold  text-black w-full inline-block leading-3 relative bottom-1">
                                    {med.last_admit_date && <span>Last administered: {med.last_admit_date} </span>} {med?.is_kop === 'T' && <span className="font-bold">Qty given: {med?.last_admit_qty}</span>}
                                  </span>}
                                </div>
                              </div>
                              {med?.status === 'I' && <div className="text-sm  px-3 pb-3 text-grey_color">* This order is not reviewed by the Pharmacist</div>}
                            </div>
                          </>
                        );
                      })}
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {showEmptyState && (
          <div className="w-full flex justify-center">
            {" "}
            <img alt="" className="" src={NO_PATIENTS} />{" "}
          </div>
        )}
      </div >

      {onshowNotes &&
        <>
          <Modal
            open={onshowNotes}
            onClose={() => {
              setOnshowNotes(false);
              setNotesData('');
            }}
            center
            styles={{
              modal: {
                height: "450px",
                overflowY: "visible",
                width: "650px"
              }
            }}
          >
            <div className="pt-6 px-8 pb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes<span className="text-xs md:text-sm text-hlred ml-1">*</span></label>
              <textarea
                className="w-full border border-gray-300 rounded-md p-2 focus:ring-indigo-500 focus:border-indigo-500"
                rows="8"
                value={notesData}
                onChange={handleNotesChange}
                placeholder="Enter notes here..."
              />
            </div>

            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => onSubmitMedicationsClick()}
                type="button"
                disabled={loading || notesData.trim() === ''}
                className={
                  (loading || notesData.trim() === '')
                    ? "opacity-50 cursor-not-allowed pointer-events-none mr-0.5 rounded font-medium h-10 text-white w-auto px-6 bg-secondary bg-login_btn focus:outline-none"
                    : "mr-0.5 rounded font-medium h-10 text-white w-auto px-6 bg-secondary bg-login_btn focus:outline-none"
                }
              >
                {loading ? "Submitting..." : "Submit"}
              </button>
            </div>
          </Modal>
        </>
      }
    </>
  );
};

export default withRouter(
  connect(
    () => ({}),
    (dispatch) => {
      return bindActionCreators({}, dispatch);
    }
  )(AddendumMedpassAdministration)
);
